/**
 * 備份與還原管理頁面
 *
 * 功能：
 * - 建立資料庫備份
 * - 查看備份列表
 * - 驗證備份完整性
 * - 還原資料庫（Super Admin 專用）
 * - 刪除備份
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Checkbox,
  TextField,
  Stack,
} from '@mui/material';
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  CheckCircle as ValidateIcon,
  CheckCircle,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  listBackups,
  createBackup,
  deleteBackup,
  restoreBackup,
  validateBackup,
  BackupInfo,
} from '../../api/admin';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminBackupPage: React.FC = () => {
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 建立備份對話框
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [includeOptional, setIncludeOptional] = useState<boolean>(false);
  const [backupName, setBackupName] = useState<string>('');
  const [compress, setCompress] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);

  // 還原對話框
  const [restoreDialogOpen, setRestoreDialogOpen] = useState<boolean>(false);
  const [restoreBackupId, setRestoreBackupId] = useState<string>('');
  const [createBackupBeforeRestore, setCreateBackupBeforeRestore] = useState<boolean>(true);
  const [restoring, setRestoring] = useState<boolean>(false);

  // 刪除確認對話框
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteBackupId, setDeleteBackupId] = useState<string>('');
  const [deleting, setDeleting] = useState<boolean>(false);

  // 驗證對話框
  const [validateDialogOpen, setValidateDialogOpen] = useState<boolean>(false);
  const [validateBackupId, setValidateBackupId] = useState<string>('');
  const [validationResult, setValidationResult] = useState<any>(null);
  const [validating, setValidating] = useState<boolean>(false);

  // 載入備份列表
  const loadBackups = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await listBackups();
      if (response.success) {
        setBackups(response.backups);
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('載入備份列表失敗:', err);
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  // 初始載入
  useEffect(() => {
    loadBackups();
  }, []);

  // 建立備份
  const handleCreateBackup = async () => {
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await createBackup({
        include_optional: includeOptional,
        backup_name: backupName || undefined,
        compress: compress,
      });

      if (response.success) {
        setSuccess(`備份建立成功：${response.backup_id}`);
        setCreateDialogOpen(false);
        setBackupName('');
        loadBackups(); // 重新載入列表
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('建立備份失敗:', err);
      setError(err.message || '建立失敗');
    } finally {
      setCreating(false);
    }
  };

  // 刪除備份
  const handleDeleteBackup = async () => {
    setDeleting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await deleteBackup(deleteBackupId);

      if (response.success) {
        setSuccess(`備份已刪除：${deleteBackupId}`);
        setDeleteDialogOpen(false);
        loadBackups(); // 重新載入列表
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('刪除備份失敗:', err);
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  };

  // 還原備份
  const handleRestoreBackup = async () => {
    setRestoring(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await restoreBackup(restoreBackupId, {
        create_backup_before_restore: createBackupBeforeRestore,
      });

      if (response.success) {
        setSuccess(`資料庫還原成功！已還原 ${response.restored_databases?.length || 0} 個資料庫`);
        setRestoreDialogOpen(false);
        loadBackups(); // 重新載入列表
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('還原備份失敗:', err);
      setError(err.message || '還原失敗');
    } finally {
      setRestoring(false);
    }
  };

  // 驗證備份
  const handleValidateBackup = async (backupId: string) => {
    setValidateBackupId(backupId);
    setValidating(true);
    setValidationResult(null);
    setValidateDialogOpen(true);

    try {
      const response = await validateBackup(backupId);

      if (response.success) {
        setValidationResult(response);
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      console.error('驗證備份失敗:', err);
      setError(err.message || '驗證失敗');
    } finally {
      setValidating(false);
    }
  };

  // 格式化檔案大小
  const formatSize = (mb: number): string => {
    if (mb < 1024) {
      return `${mb.toFixed(2)} MB`;
    } else {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
  };

  // 格式化日期
  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString('zh-TW');
    } catch {
      return dateString;
    }
  };

  return (
    <AdminLayout>
      <Box>
      {/* 標題列 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          備份與還原
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadBackups}
            disabled={loading}
          >
            重新整理
          </Button>
          <Button
            variant="contained"
            startIcon={<BackupIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            建立備份
          </Button>
        </Stack>
      </Box>

      {/* 錯誤提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 成功提示 */}
      {success && (
        <Alert severity="success" sx={{ mb: 2}} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* 備份列表 */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            備份記錄 ({backups.length})
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : backups.length === 0 ? (
            <Alert severity="info">尚無備份記錄</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>備份 ID</TableCell>
                    <TableCell>建立時間</TableCell>
                    <TableCell>大小</TableCell>
                    <TableCell>包含資料庫</TableCell>
                    <TableCell>狀態</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.backup_id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {backup.backup_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(backup.created_at)}</TableCell>
                      <TableCell>{formatSize(backup.total_size_mb)}</TableCell>
                      <TableCell>
                        {backup.databases && Object.keys(backup.databases).length > 0 ? (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {Object.keys(backup.databases).map((db) => (
                              <Chip key={db} label={db} size="small" variant="outlined" />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            未知
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {backup.is_compressed ? (
                          <Chip label="已壓縮" size="small" color="info" />
                        ) : (
                          <Chip label="未壓縮" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="驗證完整性">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleValidateBackup(backup.backup_id)}
                            >
                              <ValidateIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="還原資料庫">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setRestoreBackupId(backup.backup_id);
                                setRestoreDialogOpen(true);
                              }}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="刪除備份">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteBackupId(backup.backup_id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 建立備份對話框 */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>建立資料庫備份</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="備份名稱（選填）"
              value={backupName}
              onChange={(e) => setBackupName(e.target.value)}
              placeholder="例如：before_update"
              fullWidth
              helperText="留空則使用時間戳作為名稱"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeOptional}
                  onChange={(e) => setIncludeOptional(e.target.checked)}
                />
              }
              label="包含可選資料庫（StarsData_Male/Female, GongDescription_V2）"
            />

            <FormControlLabel
              control={
                <Checkbox checked={compress} onChange={(e) => setCompress(e.target.checked)} />
              }
              label="壓縮備份檔案（節省空間）"
            />

            <Alert severity="info" icon={<InfoIcon />}>
              核心資料庫（UserSystem, WeeklyLife, ZiWei_Questions）將自動備份。
              <br />
              可選資料庫檔案較大（約 5.8 GB），建議只在必要時備份。
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateBackup}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <BackupIcon />}
          >
            {creating ? '建立中...' : '建立備份'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 還原備份對話框 */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>還原資料庫</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Alert severity="warning">
              <Typography variant="body2" fontWeight="bold">
                警告：此操作將覆蓋當前的資料庫！
              </Typography>
              <Typography variant="body2">
                備份 ID：{restoreBackupId}
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={createBackupBeforeRestore}
                  onChange={(e) => setCreateBackupBeforeRestore(e.target.checked)}
                />
              }
              label="還原前先備份當前資料庫（強烈建議）"
            />

            <Alert severity="info" icon={<InfoIcon />}>
              只有 Super Admin 可以執行還原操作。
              <br />
              還原前會自動建立當前資料庫的備份，以防萬一。
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            取消
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRestoreBackup}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
          >
            {restoring ? '還原中...' : '確認還原'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除備份對話框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>刪除備份</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning">
              確定要刪除此備份嗎？此操作無法復原。
            </Alert>
            <Typography variant="body2">
              備份 ID：<strong>{deleteBackupId}</strong>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteBackup}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? '刪除中...' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 驗證備份對話框 */}
      <Dialog open={validateDialogOpen} onClose={() => setValidateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>驗證備份完整性</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2">
              備份 ID：<strong>{validateBackupId}</strong>
            </Typography>

            {validating ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : validationResult ? (
              <>
                <Alert
                  severity={validationResult.all_valid ? 'success' : 'error'}
                  icon={validationResult.all_valid ? <CheckCircle /> : <InfoIcon />}
                >
                  {validationResult.message}
                </Alert>

                {validationResult.validation_results && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>資料庫</TableCell>
                          <TableCell>狀態</TableCell>
                          <TableCell>訊息</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResult.validation_results.map((result: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{result.database}</TableCell>
                            <TableCell>
                              <Chip
                                label={result.valid ? '有效' : '無效'}
                                size="small"
                                color={result.valid ? 'success' : 'error'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{result.message}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateDialogOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminLayout>
  );
};

export default AdminBackupPage;
